import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ReactiveFormsModule } from '@angular/forms';
import { ModelVersion } from '../../../models/model-version.model';
import { ModelVersionService } from '../../../services/model-version.service';
import { ModelService } from '../../../services/model.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-model-version-create-or-update',
  standalone: true,
  templateUrl: './model-version-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToolbarModule,
    ToastModule,
  ],
})
export class ModelVersionCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private modelVersionService = inject(ModelVersionService);
  private modelService = inject(ModelService);
  private formGroupService = inject(FormGroupService);

  private readonly maxImageSizeBytes = 5 * 1024 * 1024; // 5MB

  loading = signal(false);

  modelVersionId = signal<string | null>(null);

  selectLoading = signal(false);

  modelsDropdown = signal<DropdownOption[]>([]);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required]],
    description: [null],
    modelId: [null, [Validators.required]],
    image: [null],
    imageFileId: [null],
    imageUrl: [null],
    isActive: [true],
  });

  selectedImagePreviewUrl = signal<string | null>(null);
  private selectedImageObjectUrl: string | null = null;

  ngOnInit(): void {
    this.loadModelsDropdown();

    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.modelVersionId.set(id);
        this.loadData(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokeSelectedLogoObjectUrl();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!file.type!.startsWith('image/')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `O arquivo deve ser uma imagem (ex: jpg, png, webp, etc.).`,
        life: 5000,
      });
      return;
    }

    if (file.size > this.maxImageSizeBytes) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `A image deve ter no máximo 5MB.`,
        life: 5000,
      });
      return;
    }

    if (
      this.formGroup.get('imageUrl')?.value ||
      this.formGroup.get('imageFileId')?.value
    ) {
      this.formGroup.patchValue({
        imageUrl: null,
        imageFileId: null,
      });
    }

    this.revokeSelectedLogoObjectUrl();
    this.selectedImageObjectUrl = URL.createObjectURL(file);
    this.selectedImagePreviewUrl.set(this.selectedImageObjectUrl);

    this.formGroup.patchValue({ image: file });
    this.formGroup.get('image')!.updateValueAndValidity();
  }

  removeLogo(): void {
    this.formGroup.patchValue({
      image: null,
      imageFileId: null,
      imageUrl: null,
    });

    this.formGroup.get('image')?.updateValueAndValidity();

    this.revokeSelectedLogoObjectUrl();
    this.selectedImagePreviewUrl.set(null);
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.modelVersionId();
    const formData = this.buildFormData();

    this.loading.set(true);

    const request$ = id
      ? this.modelVersionService.update(id, formData)
      : this.modelVersionService.create(formData);

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Versão salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/model-versions']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a versão',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadModelsDropdown(): void {
    this.selectLoading.set(true);

    this.modelService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.modelsDropdown.set(options);
        this.selectLoading.set(false);
      },
      error: () => {
        this.selectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar os modelos',
          life: 3000,
        });
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.modelVersionService.getById(id).subscribe({
      next: (response: ApiResult) => {
        const modelVersion = response.data as ModelVersion;

        this.formGroup.patchValue(modelVersion);
        this.loading.set(false);
        this.formGroup.get('modelId')?.setValue({
          label: modelVersion.modelName,
          value: modelVersion.modelId,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a versão',
            life: 3000,
          });
        }
      },
    });
  }

  private buildFormData(): FormData {
    const formValue = this.formGroup.getRawValue();
    const formData = new FormData();

    formData.append('name', formValue.name);
    formData.append('description', formValue.description ?? '');
    formData.append('modelId', formValue.modelId?.value);

    if (formValue.id) formData.append('id', formValue.id);
    if (formValue.image) formData.append('image', formValue.image);
    if (formValue.imageFileId)
      formData.append('imageFileId', formValue.imageFileId);

    if (this.modelVersionId() !== null)
      formData.append('isActive', formValue.isActive.toString());

    return formData;
  }

  private revokeSelectedLogoObjectUrl(): void {
    if (this.selectedImageObjectUrl) {
      URL.revokeObjectURL(this.selectedImageObjectUrl);
      this.selectedImageObjectUrl = null;
    }
  }
}
